/*
 * Copyright (c) 2020 Honeywell International, Inc. All rights reserved.
 *
 * This file contains trade secrets of Honeywell International, Inc.  No part
 *  may be reproduced or transmitted in any form by any means or for any
 *  purpose without the express written permission of Honeywell.
 *
 */

import * as React from 'react';
import {Component} from "react";
import {inject, observer} from "mobx-react";
import {
  ISiteDetails,
  ISiteSelectionProps,
  ISiteSelectionState
} from "../../../stores/GatewayManager/IGatewayRegistrationInterfaces";
import {
  Button, Input, Modal,
  Progress,
  Radio, Search
} from "@scuf/common";

import './SiteSelection.scss';
import {toJS} from "mobx";
import ConfirmSiteSelection from './ConfirmSiteSelection';

/**
 *  The user interface component for site selection
 */
@inject('siteSelectionStore')
@observer
export default class SiteSelection extends Component<ISiteSelectionProps, ISiteSelectionState> {

  isValid : Boolean = true;

  /**
   * Constructor injection of site selection props
   */
  constructor(props: ISiteSelectionProps) {
    super(props);
    this.state = {
      sitesLoaded: false,
      seekConfirmation: false,
      selectedSite: {
        siteId: '',
        siteName: '',
        gatewayName:'',
        customerDetails: {
          organizationId: ''
        }
      },
      errors: {}
    };
  }

  /**
   * Invoke the API to get the list of sites.
   */
  componentDidMount() {
    let siteSelectionStore = this.props.siteSelectionStore;
    siteSelectionStore!.getSiteList().finally(() => {
      let sites = toJS(siteSelectionStore!.siteList);
      if (!this.state.selectedSite.siteId && sites.length > 0) {
        this.setState({
          selectedSite: sites[0]
        })
      }
      this.setState({sitesLoaded: true});
    });

    this.validate();
  }

  /**
   * Validate on component mount
   */
  validate(){
    this.handleChange(this.state.selectedSite.gatewayName);
    this.handleValidation()
  }

  /**
   *  Render the site selection user interface
   */
  render() {
    return (
      <React.Fragment>
        <div className='application-page-content ui-site-selection-container'>
          <div>
            {this.getDeviceInfo()}
            {(this.state.sitesLoaded) ? this.renderGatewayName() : ""}
            <div className='ui-site-search'>
              <Search results={[]}
                // A workaround for time being since there s no provision to hide results
                // The solution is to override "resultsRenderer" and display the site results in conjunction with search.
                      noResultsMessage={"\t"}
                      placeholder='Search for sites or address'
                      onSearchChange={(value: string = '') => {
                        this.props.siteSelectionStore!.setSiteFilter(value);
                      }}
                      onResultClear={() => {
                        this.props.siteSelectionStore!.setSiteFilter('');
                      }}
              />
            </div>
          </div>
          <div className='ui-site-list'>
            {
              (this.state.sitesLoaded) ? this.renderSiteList() :
                <div className='ui-sites-loading'>
                  <Progress circular={true} size={"xlarge"}/>
                </div>
            }
          </div>
        </div>
        <div className='application-navigation ui-box-shadow'>
          <div className="ui-site-selection-navigation">
            <Button type="primary"
                    onClick={() => {
                      this.setState({
                        seekConfirmation: true
                      });
                    }}
                    className='register-gateway-btn'
                    disabled={!this.state.selectedSite.siteId || !this.isValid}
                    content={`${SiteSelection.REGISTER_GATEWAY}`}/>
          </div>
        </div>
        {this.renderSiteChoiceConfirmation()}
      </React.Fragment>
    );
  }

  /**
   * renderer for the site data
   * @param {ICellData} cellData
   * @returns {any}
   */
  renderSiteList = () => {
    let sites = this.props.siteSelectionStore!.filteredSiteList;
    let siteIdSelected = this.state.selectedSite.siteId;
    return sites.map((site: ISiteDetails, index: number) => {
      let isSelected = (siteIdSelected) ? (siteIdSelected === site.siteId) : (index === 0);
      return (
        <div
          className={(isSelected) ? 'ui-site-block-selected' : 'ui-site-block-unselected'}
          key={`main${index}`} onClick={() => { 
          site.gatewayName = this.state.selectedSite.gatewayName;  
          this.setState({selectedSite: site});
        }}>
          <div className='ui-site-select-radio' key={`blk${index}`}>
            <Radio checked={isSelected}
                   key={`r${index}`}
            />
          </div>
          <div className='ui-site-block-details' key={`bd${index}`}>
            <div className='ui-site-details-name' key={`sd${index}`}>
              {site.siteName}
            </div>
            <div className='ui-site-details-address' key={`sda1${index}`}>
              {site.address1 || site.customerDetails.address1}
            </div>
            <div className='ui-site-details-address' key={`sda2${index}`}>
              {site.address2 || site.customerDetails.address2}
            </div>
          </div>
        </div>
      );
    });
  };

  /**
   * Render the modal to ask for user confirmation on site select
   */
  renderSiteChoiceConfirmation = () => {
    return <Modal closeIcon={true}
                  size='small'
                  className='site-confirm-modal'
                  onClose={() => this.setState({seekConfirmation: false})}
                  open={this.state.seekConfirmation}
                  closeOnDimmerClick={false}>
      <Modal.Header className='confirm-site-header'>
        <div className='ui-confirm-site-title'>
          Confirm Selection
        </div>
      </Modal.Header>
      <Modal.Content>
        <div className='selected-site'>
        <ConfirmSiteSelection selectedSite={this.state.selectedSite}/>
        </div>
      </Modal.Content>
      <Modal.Footer className='site-selection-actions'>
          <Button type="secondary" size="small" className="site-secondary-button" content="Cancel"
                  onClick={() => this.setState({seekConfirmation: false})}/>
          <Button type="primary" size="small" className="site-primary-button" content="Confirm"
                  onClick={() => {
                    this.initializeRegistration();
                    this.setState({seekConfirmation: false});
                  }}/>
      </Modal.Footer>
    </Modal>;
  };

  /**
   * Initialize the device registration process
   */
  initializeRegistration() {
    this.props.siteSelected(this.state.selectedSite);
  }

  /**
   * retur the components that make up the device section of the page.
   * @returns {(JSX.Element)[]}
   */
  private getDeviceInfo() {
    return <React.Fragment>
      <div className='ui-site-selection-title'>
        {SiteSelection.TITLE}
      </div>
      <div className='ui-site-selection-message'>
        {SiteSelection.SUB_TITLE}
      </div>
      <div className='ui-serialnumber-title'>
        {SiteSelection.SERIAL_NUMBER_TITLE}
      </div>
      <div className='ui-serialnumber-value'>
        {this.props.serialNumber}
      </div>
    </React.Fragment>;
  }

  /**
   * Render Gateway Name
   * @returns {(JSX.Element)[]}
   */
  renderGatewayName() {
    return(
      <React.Fragment>
        <div className='ui-gateway-name-input'>
            <Input placeholder="Provide gateway name" label="Gateway Name"
                   onChange={( value: any ) =>{this.onChangeEvent(value)}}
                   error={this.state.errors[SiteSelection.GATEWAY_NAME]}
            />
        </div>
      </React.Fragment>  
    )
  }

  /*
    - Perform below actions on change
  */
  onChangeEvent(value:any){
      this.handleChange(value);
      this.handleValidation();
  }

  /*
    - Handle change event
  */
  handleChange(value:any){ 
    let siteDetails = this.state.selectedSite;
    siteDetails['gatewayName'] = value;      
    this.setState({selectedSite : siteDetails});
  }

  /*
    - Handle validation
  */
  handleValidation(){
    let selectedSite = this.state.selectedSite;
    let errors : any = {};
    this.isValid = true;
    
    //gatewayName mandatory
    if(!selectedSite[SiteSelection.GATEWAY_NAME]!.trim()){
        this.isValid = false;
        errors[SiteSelection.GATEWAY_NAME] = SiteSelection.ERROR_CANNOT_BE_BLANK;
    }

    //gatewayName max length 90
    if(selectedSite[SiteSelection.GATEWAY_NAME]){
      if(selectedSite[SiteSelection.GATEWAY_NAME]!.length > SiteSelection.MAX_LENGTH){
          this.isValid = false;
          errors[SiteSelection.GATEWAY_NAME] = SiteSelection.ERROR_MAX_LENGTH;
      }
    }
    this.setState({errors: errors});
    return this.isValid;
  }

  public static readonly TITLE = 'Select your location';

  public static readonly SUB_TITLE = 'We found the following locations associated with your Honeywell Forge account. ' +
    'Where is your gateway located?';

  public static readonly SERIAL_NUMBER_TITLE = "The Gateway's Serial Number is";

  public static readonly REGISTER_GATEWAY = "Register Gateway";

  public static readonly GATEWAY_NAME = "gatewayName";

  public static readonly MAX_LENGTH = 90;

  public static readonly ERROR_CANNOT_BE_BLANK = "Cannot be blank";

  public static readonly ERROR_MAX_LENGTH = "Cannot exceed max length 90";
}
